// =============================================
// Thematic Map AI Service — wywołania Claude API
// =============================================

import type {
  ThematicMap,
  ThematicSeed,
  ThematicNode,
  LLMMacroOutput,
  LLMSeedOutput,
  LLMNodeOutput,
  LLMEntityOutput,
  LLMRelationOutput,
  LLMContentPlanOutput,
  LLMContentFullOutput,
  LLMEAVOutput,
  LLMEAVAttributeOutput,
  LLMDeduplicationOutput,
  PromptStep,
  PromptModel,
  DocumentRole,
  EntityType,
  RelationType,
  EAVValueType,
} from "@/types/thematicMaps";
import {
  DOCUMENT_ROLES,
  ENTITY_TYPES,
  RELATION_TYPES,
  EAV_VALUE_TYPES,
} from "@/types/thematicMaps";
import { logDebug, logDebugWarn, logError, logPublic } from "@/services/contentEngine/contentLogger";
import { getApiTarget } from "@/lib/apiBase";
import { supportsSampling, thinkingConfigFor } from "@/services/llm/claudeProvider";
import { withDateVars } from "@/lib/dateVars";

// Runtime-aware URL: worker (Node) wola Anthropic bezposrednio z env key;
// browser uzywa nginx proxy ktore wstrzykuje klucz server-side per X-Workspace.
// Phase 3 C2 (2026-05-26): zunifikowane przez getApiTarget z apiBase.ts —
// X-Workspace header dodawany automatycznie, headers z runtime-aware logic.
const IS_NODE =
  typeof process !== "undefined" &&
  typeof process.versions === "object" &&
  typeof process.versions.node === "string";
const DEFAULT_MODEL: PromptModel = "claude-sonnet-5";
const MAX_TOKENS = 4096;
const MAX_TOKENS_SEEDS = 8192; // v3 seed_generation moze zwrocic 15-25+ seedow z seed_type + scope_coverage
const MAX_TOKENS_LARGE = 16384; // For entity/relation generation (large outputs)
const TEMPERATURE = 0.7;
// 3 min — krocej czekamy zeby szybciej zlapac zatkany call.
// Przy retry x1 + backoff 5-7s, to lepiej zabic call po 180s i sprobowac drugi
// raz, niz wisiec 10 min. Jak nadal pada — user obniza batch_size w Settings.
const FETCH_TIMEOUT_MS = 180_000;

/** Mapowanie PromptModel -> pełny model ID dla API */
const MODEL_IDS: Record<PromptModel, string> = {
  "claude-opus-5": "claude-opus-5",
  "claude-sonnet-5": "claude-sonnet-5",
  "claude-opus-4-8": "claude-opus-4-8",
  "claude-opus-4-6": "claude-opus-4-6",
  "claude-sonnet-4-6": "claude-sonnet-4-6",
  "claude-sonnet-4-5": "claude-sonnet-4-5-20250929",
  "claude-sonnet-4-0": "claude-sonnet-4-0",
  "claude-haiku-4-5": "claude-haiku-4-5-20251001",
} as Record<PromptModel, string>;

export interface GenerationResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  tokensInput?: number;
  tokensOutput?: number;
  modelUsed?: string;
}

// =============================================
// JSON Schemas dla structured output
// =============================================

const MACRO_SCHEMA = {
  type: "object",
  properties: {
    macro_statement: { type: "string" },
    scope_in: { type: "array", items: { type: "string" }, minItems: 1 },
    scope_out: { type: "array", items: { type: "string" }, minItems: 1 },
  },
  required: ["macro_statement", "scope_in", "scope_out"],
  additionalProperties: false,
};

const SEED_SCHEMA = {
  type: "array",
  items: {
    type: "object",
    properties: {
      name: { type: "string" },
      slug: { type: "string", pattern: "^[a-z0-9-]+$" },
      intent_family: { type: "array", items: { type: "string" }, minItems: 1 },
      priority: { type: "number" },
      // v3 — SRO framework metadata (optional for backward compat)
      seed_type: {
        type: "string",
        enum: [
          "definicyjna", "procesowa", "typologia", "skladniki", "narzedzia",
          "problemy", "bezpieczenstwo", "historia", "trendy", "kontekst",
        ],
      },
      scope_coverage: {
        type: "array",
        items: { type: "string" },
      },
    },
    required: ["name", "slug", "intent_family", "priority"],
    additionalProperties: false,
  },
  minItems: 1,
};

const NODE_SCHEMA = {
  type: "array",
  items: {
    type: "object",
    properties: {
      title: { type: "string" },
      slug: { type: "string", pattern: "^[a-z0-9-]+$" },
      document_role: {
        type: "string",
        enum: [
          "definition",
          "how_to",
          "comparison",
          "troubleshooting",
          "glossary",
          "history",
          "commercial",
          "protocol",
          "guide",
        ],
      },
      primary_intent: { type: "string" },
      primary_entity: { type: "string" },
      canonical_question: { type: "string", pattern: ".*\\?$" },
      required_entities: {
        type: "array",
        items: { type: "string" },
        minItems: 1,
      },
    },
    required: [
      "title",
      "slug",
      "document_role",
      "canonical_question",
      "primary_entity",
      "required_entities",
    ],
    additionalProperties: false,
  },
  minItems: 1,
};

const ENTITY_SCHEMA = {
  type: "array",
  items: {
    type: "object",
    properties: {
      canonical_name: { type: "string" },
      entity_type: {
        type: "string",
        enum: [
          "concept",
          "product",
          "method",
          "tool",
          "role",
          "problem",
          "attribute",
        ],
      },
      aliases: { type: "array", items: { type: "string" } },
      description: { type: "string" },
      is_scope_entity: { type: "boolean" },
    },
    required: ["canonical_name", "entity_type", "aliases", "description"],
    additionalProperties: false,
  },
  minItems: 1,
};

const RELATION_SCHEMA = {
  type: "array",
  items: {
    type: "object",
    properties: {
      from_slug: { type: "string" },
      to_slug: { type: "string" },
      relation_type: {
        type: "string",
        enum: [
          "parent_of",
          "sibling",
          "prerequisite",
          "next_step",
          "comparison",
          "glossary_ref",
          "entity_ref",
        ],
      },
      weight: { type: "number" },
      reason: { type: "string" },
    },
    required: ["from_slug", "to_slug", "relation_type", "weight"],
    additionalProperties: false,
  },
  minItems: 1,
};

const CONTENT_PLAN_SCHEMA = {
  type: "object",
  properties: {
    seo_title: { type: "string" },
    meta_description: { type: "string" },
    opening_answer: { type: "string" },
    h2_outline: { type: "array", items: { type: "string" }, minItems: 1 },
    key_points: { type: "array", items: { type: "string" }, minItems: 1 },
    faq: { type: "array", items: { type: "string" } },
    do_not_include: { type: "array", items: { type: "string" } },
    internal_links: {
      type: "array",
      items: {
        type: "object",
        properties: {
          slug: { type: "string" },
          title: { type: "string" },
          anchor: { type: "string" },
          reason: { type: "string" },
        },
        required: ["slug", "title", "anchor", "reason"],
        additionalProperties: false,
      },
    },
  },
  required: [
    "seo_title",
    "meta_description",
    "opening_answer",
    "h2_outline",
    "key_points",
    "faq",
    "do_not_include",
    "internal_links",
  ],
  additionalProperties: false,
};

const CONTENT_FULL_SCHEMA = {
  type: "object",
  properties: {
    content_markdown: { type: "string" },
  },
  required: ["content_markdown"],
  additionalProperties: false,
};

const DEDUP_SCHEMA = {
  type: "object",
  properties: {
    same_intent: { type: "string", enum: ["yes", "no", "maybe"] },
    confidence: { type: "number" },
    reason: { type: "string" },
  },
  required: ["same_intent", "confidence", "reason"],
  additionalProperties: false,
};

const EAV_SCHEMA = {
  type: "object",
  properties: {
    entities: {
      type: "array",
      items: {
        type: "object",
        properties: {
          entity_canonical_name: { type: "string" },
          attributes: {
            type: "array",
            items: {
              type: "object",
              properties: {
                attribute_name: { type: "string" },
                value: { type: "string" },
                value_type: {
                  type: "string",
                  enum: ["text", "number", "boolean", "enum"],
                },
                confidence: { type: "number" },
              },
              required: ["attribute_name", "value", "value_type", "confidence"],
              additionalProperties: false,
            },
          },
        },
        required: ["entity_canonical_name", "attributes"],
        additionalProperties: false,
      },
    },
  },
  required: ["entities"],
  additionalProperties: false,
};

/**
 * Wywołuje Claude API z danym promptem.
 * Opcjonalnie wymusza structured JSON output przez output_config.format.
 */
async function callClaude(
  prompt: string,
  model: PromptModel = DEFAULT_MODEL,
  jsonSchema?: Record<string, unknown>,
  maxTokens: number = MAX_TOKENS,
  stepName: string = "claude_call",
  temperature: number = TEMPERATURE,
): Promise<{
  text: string;
  tokensInput: number;
  tokensOutput: number;
  modelUsed: string;
}> {
  const modelId = MODEL_IDS[model] || MODEL_IDS[DEFAULT_MODEL];
  const startTime = Date.now();

  // DEBUG: log full request details
  logDebug("CLAUDE", `${stepName} REQUEST`, {
    model: modelId,
    max_tokens: maxTokens,
    temperature,
    prompt_length: prompt.length,
    has_json_schema: !!jsonSchema,
    prompt_full: prompt, // FULL PROMPT in log (debug only)
  });

  const body: Record<string, unknown> = {
    model: modelId,
    max_tokens: maxTokens,
    messages: [{ role: "user", content: prompt }],
  };
  // Claude 5 (opus-5/sonnet-5) i Opus 4.7/4.8 odrzucaja sampling params (400) —
  // ten sam guard co supportsSampling() w claudeProvider (docs: model-deprecations).
  if (supportsSampling(modelId)) {
    body.temperature = temperature;
  }

  // Claude 5: wylacz domyslne adaptive thinking — tokeny myslenia licza sie do
  // max_tokens i przy limitach 4096-16384 ucialy odpowiedz ("4096 z 4096").
  // {type:"disabled"} = dokladnie stare zachowanie (4.5/4.6 nie myslaly).
  const thinking = thinkingConfigFor(modelId);
  if (thinking) {
    body.thinking = thinking;
  }

  // Structured JSON output — wymusza poprawny JSON (Anthropic API: output_config.format)
  if (jsonSchema) {
    body.output_config = {
      format: {
        type: "json_schema",
        schema: jsonSchema,
      },
    };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let response: Response;
  try {
    // Worker (Node): x-api-key z env. Browser: nginx proxy wstrzyka klucz
    // per X-Workspace (getApiTarget zwraca poprawne headers + URL).
    const target = getApiTarget("anthropic", "/v1/messages");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "anthropic-version": "2023-06-01",
      ...target.headers, // Node: x-api-key + anthropic-version. Browser: X-Workspace.
    };
    if (!IS_NODE) {
      // Browser-only — w Node nie potrzebne (dla CORS bypass). Anthropic rejectuje
      // ten header przy directcall, a w browser musi byc bo SDK tak chce.
      headers["anthropic-dangerous-direct-browser-access"] = "true";
    }
    // x-api-key: w Node target.headers juz zawiera (z nodeAuthHeaders w apiBase).
    // W browser: brak (nginx wstrzykuje server-side per X-Workspace).

    response = await fetch(target.url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      logError("CLAUDE", `${stepName} TIMEOUT after ${FETCH_TIMEOUT_MS / 1000}s`);
      throw new Error(
        `Claude API timeout — brak odpowiedzi w ciągu ${FETCH_TIMEOUT_MS / 1000}s. Spróbuj ponownie.`,
      );
    }
    // undici "fetch failed" wraps real cause w err.cause (np. {code:'ECONNRESET'})
    // Bez tego logu user widzi tylko "fetch failed — fetch failed" (bezuzyteczne).
    const cause = err?.cause?.code || err?.cause?.message || err?.message || "unknown";
    logError("CLAUDE", `${stepName} fetch failed (cause: ${cause})`, err);
    throw new Error(`Claude API fetch failed (${cause}) — moze rate limit albo network reset`);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json();
    logError("CLAUDE", `${stepName} HTTP ${response.status}`, errorData);
    throw new Error(
      `Claude API error: ${response.status} - ${errorData.error?.message || "Unknown"}`,
    );
  }

  const result = await response.json();
  const duration = Date.now() - startTime;

  // Sprawdź czy output został ucięty (stop_reason: "max_tokens")
  if (result.stop_reason === "max_tokens") {
    logError("CLAUDE", `${stepName} TRUNCATED — used ${result.usage?.output_tokens}/${maxTokens} tokens`);
    throw new Error(
      `Odpowiedź została ucięta (za dużo danych). ` +
        `Użyto ${result.usage?.output_tokens || "?"} tokenów z limitu ${maxTokens}. ` +
        `Spróbuj z mniejszą liczbą danych wejściowych.`,
    );
  }

  const text = result.content?.[0]?.text?.trim() || "";

  // DEBUG: log full response + metrics
  logDebug("CLAUDE", `${stepName} RESPONSE`, {
    model: modelId,
    duration_ms: duration,
    tokens_in: result.usage?.input_tokens || 0,
    tokens_out: result.usage?.output_tokens || 0,
    stop_reason: result.stop_reason || "unknown",
    response_length: text.length,
    response_full: text, // FULL RESPONSE in log (debug only)
  });
  logPublic(stepName, `${modelId} — ${result.usage?.input_tokens || 0}→${result.usage?.output_tokens || 0} tokens, ${(duration / 1000).toFixed(1)}s`);

  return {
    text,
    tokensInput: result.usage?.input_tokens || 0,
    tokensOutput: result.usage?.output_tokens || 0,
    modelUsed: modelId,
  };
}

/**
 * Parsuje JSON z odpowiedzi Claude.
 * Obsługuje markdown code blocks (```json ... ```)
 */
function parseJSON<T>(text: string): T {
  let cleaned = text.trim();

  // Usun markdown code block
  const jsonBlockMatch = cleaned.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (jsonBlockMatch) {
    cleaned = jsonBlockMatch[1].trim();
  }

  return JSON.parse(cleaned);
}

/**
 * Wypełnia zmienne w template promptu.
 */
export function fillPromptTemplate(
  template: string,
  vars: Record<string, string>,
): string {
  let filled = template;
  // current_year/current_date doklejane automatycznie (fix zlego roku w naglowkach)
  for (const [key, value] of Object.entries(withDateVars(vars))) {
    filled = filled.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
  }
  return filled;
}

// =============================================
// Helpers
// =============================================

/** Sanityzuje slug — transliteracja polskich znaków, lowercase, tylko [a-z0-9-] */
export function sanitizeSlug(raw: string): string {
  const POLISH: Record<string, string> = {
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ó: "o",
    ś: "s",
    ź: "z",
    ż: "z",
    Ą: "A",
    Ć: "C",
    Ę: "E",
    Ł: "L",
    Ń: "N",
    Ó: "O",
    Ś: "S",
    Ź: "Z",
    Ż: "Z",
  };
  return raw
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (m) => POLISH[m] || m)
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

// =============================================
// Walidatory outputu LLM
// =============================================

function validateMacroOutput(data: any): LLMMacroOutput {
  if (!data.macro_statement || typeof data.macro_statement !== "string") {
    throw new Error("macro_statement jest wymagane");
  }
  return {
    macro_statement: data.macro_statement,
    scope_in: Array.isArray(data.scope_in) ? data.scope_in : [],
    scope_out: Array.isArray(data.scope_out) ? data.scope_out : [],
  };
}

function validateSeedOutput(data: any): LLMSeedOutput[] {
  if (!Array.isArray(data)) throw new Error("Oczekiwano tablicy seeds");

  // v3 — LOG, NIE ODRZUCAJ. Minimum 1 seed, brak gornego limitu.
  if (data.length < 3) {
    logDebugWarn("Seeds", `Malo seedow: ${data.length} (oczekiwano 3+, ale przechodza dalej)`);
  }
  if (data.length > 40) {
    logDebugWarn("Seeds", `Duzo seedow: ${data.length} (>40, ale przechodza dalej)`);
  }
  if (data.length === 0) throw new Error("Pusta tablica seeds — LLM nic nie zwrocil");

  const slugs = new Set<string>();

  return data.map((s: any, i: number) => {
    if (!s.name || !s.slug)
      throw new Error(`Seed #${i + 1}: brak name lub slug`);
    s.slug = sanitizeSlug(s.slug);
    if (!s.slug) throw new Error(`Seed #${i + 1}: slug pusty po sanityzacji`);
    if (s.slug.length > 80)
      throw new Error(`Seed #${i + 1}: slug za długi (max 80)`);
    if (slugs.has(s.slug))
      throw new Error(`Seed #${i + 1}: duplikat slug "${s.slug}"`);
    slugs.add(s.slug);

    const out: LLMSeedOutput = {
      name: s.name,
      slug: s.slug,
      intent_family: Array.isArray(s.intent_family) ? s.intent_family : [],
      priority:
        typeof s.priority === "number"
          ? Math.min(5, Math.max(1, s.priority))
          : 2,
    };

    // v3 — optional SRO metadata
    if (typeof s.seed_type === "string") {
      out.seed_type = s.seed_type;
    }
    if (Array.isArray(s.scope_coverage)) {
      out.scope_coverage = s.scope_coverage.filter((x: any) => typeof x === "string");
    }

    return out;
  });
}

function validateNodeOutput(data: any): LLMNodeOutput[] {
  if (!Array.isArray(data)) throw new Error("Oczekiwano tablicy nodes");

  const slugs = new Set<string>();

  return data.map((n: any, i: number) => {
    if (!n.title || !n.slug)
      throw new Error(`Node #${i + 1}: brak title lub slug`);
    if (!n.canonical_question)
      throw new Error(`Node #${i + 1}: brak canonical_question`);

    n.slug = sanitizeSlug(n.slug);
    if (!n.slug) throw new Error(`Node #${i + 1}: slug pusty po sanityzacji`);
    if (slugs.has(n.slug))
      throw new Error(`Node #${i + 1}: duplikat slug "${n.slug}"`);
    slugs.add(n.slug);

    // Normalizacja: LLM czasem zwraca "How-To" / "Definition" / " guide " → znormalizuj
    // do lowercase + trim + replace dash→underscore ZANIM sprawdzasz DOCUMENT_ROLES.
    // Bez tego DB CHECK constraint wali 23514 na bulk insert.
    const roleRaw = String(n.document_role ?? "")
      .toLowerCase()
      .trim()
      .replace(/[-\s]+/g, "_");
    if (!DOCUMENT_ROLES.includes(roleRaw as DocumentRole)) {
      throw new Error(`Node #${i + 1}: nieznana rola "${n.document_role}" (normalized: "${roleRaw}")`);
    }
    const role = roleRaw as DocumentRole;

    return {
      title: n.title,
      slug: n.slug,
      document_role: role,
      primary_intent: n.primary_intent || "",
      primary_entity: n.primary_entity || "",
      canonical_question: n.canonical_question,
      required_entities: Array.isArray(n.required_entities)
        ? n.required_entities
        : [],
    };
  });
}

function validateEntityOutput(data: any): LLMEntityOutput[] {
  if (!Array.isArray(data)) throw new Error("Oczekiwano tablicy entities");

  return data.map((e: any, i: number) => {
    if (!e.canonical_name)
      throw new Error(`Entity #${i + 1}: brak canonical_name`);

    const entityType = e.entity_type as EntityType;
    if (!ENTITY_TYPES.includes(entityType)) {
      throw new Error(`Entity #${i + 1}: nieznany typ "${e.entity_type}"`);
    }

    return {
      canonical_name: e.canonical_name,
      entity_type: entityType,
      aliases: Array.isArray(e.aliases) ? e.aliases : [],
      description: e.description || "",
    };
  });
}

function validateContentPlanOutput(data: any): LLMContentPlanOutput {
  if (!data.seo_title || typeof data.seo_title !== "string") {
    throw new Error("seo_title jest wymagane");
  }
  if (!data.meta_description || typeof data.meta_description !== "string") {
    throw new Error("meta_description jest wymagane");
  }
  if (!data.opening_answer || typeof data.opening_answer !== "string") {
    throw new Error("opening_answer jest wymagane");
  }
  return {
    seo_title: data.seo_title,
    meta_description: data.meta_description,
    opening_answer: data.opening_answer,
    h2_outline: Array.isArray(data.h2_outline) ? data.h2_outline : [],
    key_points: Array.isArray(data.key_points) ? data.key_points : [],
    faq: Array.isArray(data.faq) ? data.faq : [],
    do_not_include: Array.isArray(data.do_not_include)
      ? data.do_not_include
      : [],
    internal_links: Array.isArray(data.internal_links)
      ? data.internal_links.map((l: any) => ({
          slug: l.slug || "",
          title: l.title || "",
          anchor: l.anchor || "",
          reason: l.reason || "",
        }))
      : [],
  };
}

function validateContentFullOutput(data: any): LLMContentFullOutput {
  if (!data.content_markdown || typeof data.content_markdown !== "string") {
    throw new Error("content_markdown jest wymagane");
  }
  return {
    content_markdown: data.content_markdown,
  };
}

function validateRelationOutput(data: any): LLMRelationOutput[] {
  if (!Array.isArray(data)) throw new Error("Oczekiwano tablicy relations");

  return data.map((r: any, i: number) => {
    if (!r.from_slug || !r.to_slug)
      throw new Error(`Relation #${i + 1}: brak from_slug lub to_slug`);

    const relType = r.relation_type as RelationType;
    if (!RELATION_TYPES.includes(relType)) {
      throw new Error(`Relation #${i + 1}: nieznany typ "${r.relation_type}"`);
    }

    return {
      from_slug: r.from_slug,
      to_slug: r.to_slug,
      relation_type: relType,
      weight:
        typeof r.weight === "number" ? Math.min(10, Math.max(0, r.weight)) : 1,
    };
  });
}

function validateEAVOutput(data: any): LLMEAVOutput[] {
  const entities = data?.entities;
  if (!Array.isArray(entities))
    throw new Error("Oczekiwano obiektu z tablicą entities");

  return entities.map((e: any, i: number) => {
    if (
      !e.entity_canonical_name ||
      typeof e.entity_canonical_name !== "string"
    ) {
      throw new Error(`EAV Entity #${i + 1}: brak entity_canonical_name`);
    }
    if (!Array.isArray(e.attributes)) {
      throw new Error(`EAV Entity #${i + 1}: brak tablicy attributes`);
    }

    const validatedAttrs: LLMEAVAttributeOutput[] = e.attributes
      .filter((a: any) => a.attribute_name && a.value)
      .map((a: any) => {
        const vt = a.value_type as EAVValueType;
        const validType = EAV_VALUE_TYPES.includes(vt) ? vt : "text";
        const conf =
          typeof a.confidence === "number"
            ? Math.min(1, Math.max(0, a.confidence))
            : 0.8;

        return {
          attribute_name: String(a.attribute_name).toLowerCase().trim(),
          value: String(a.value).trim(),
          value_type: validType,
          confidence: Math.round(conf * 100) / 100,
        };
      });

    return {
      entity_canonical_name: e.entity_canonical_name,
      attributes: validatedAttrs,
    };
  });
}

// =============================================
// Publiczne metody generowania
// =============================================

export async function generateMacro(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMMacroOutput>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(prompt, model, MACRO_SCHEMA, MAX_TOKENS, "macro_generation");
    const parsed = parseJSON<any>(response.text);
    const validated = validateMacroOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateSeeds(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMSeedOutput[]>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(prompt, model, SEED_SCHEMA, MAX_TOKENS_SEEDS, "seed_generation", 0.3);
    const parsed = parseJSON<any>(response.text);
    const validated = validateSeedOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateNodes(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMNodeOutput[]>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(
      prompt,
      model,
      NODE_SCHEMA,
      MAX_TOKENS_LARGE,
      "node_generation",
      0.4,
    );
    const parsed = parseJSON<any>(response.text);
    const validated = validateNodeOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateEntities(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMEntityOutput[]>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(
      prompt,
      model,
      ENTITY_SCHEMA,
      MAX_TOKENS_LARGE,
      "entity_extraction",
      0.2,
    );
    const parsed = parseJSON<any>(response.text);
    const validated = validateEntityOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateRelations(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMRelationOutput[]>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(
      prompt,
      model,
      RELATION_SCHEMA,
      MAX_TOKENS_LARGE,
      "linking",
      0.3,
    );
    const parsed = parseJSON<any>(response.text);
    const validated = validateRelationOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

/**
 * Compare two nodes for semantic duplication.
 */
export async function generateDeduplication(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMDeduplicationOutput>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(prompt, model, DEDUP_SCHEMA, MAX_TOKENS, "deduplication", 0.2);
    const parsed = parseJSON<LLMDeduplicationOutput>(response.text);

    return {
      success: true,
      data: parsed,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateContentPlan(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMContentPlanOutput>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(prompt, model, CONTENT_PLAN_SCHEMA, MAX_TOKENS, "content_plan");
    const parsed = parseJSON<any>(response.text);
    const validated = validateContentPlanOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateContentFull(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMContentFullOutput>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(
      prompt,
      model,
      CONTENT_FULL_SCHEMA,
      MAX_TOKENS_LARGE,
      "content_full",
    );
    const parsed = parseJSON<any>(response.text);
    const validated = validateContentFullOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}

export async function generateEAV(
  promptTemplate: string,
  vars: Record<string, string>,
  model?: PromptModel,
): Promise<GenerationResult<LLMEAVOutput[]>> {
  try {
    const prompt = fillPromptTemplate(promptTemplate, vars);
    const response = await callClaude(
      prompt,
      model,
      EAV_SCHEMA,
      MAX_TOKENS_LARGE,
      "eav_extraction",
      0.3,
    );
    const parsed = parseJSON<any>(response.text);
    const validated = validateEAVOutput(parsed);

    return {
      success: true,
      data: validated,
      tokensInput: response.tokensInput,
      tokensOutput: response.tokensOutput,
      modelUsed: response.modelUsed,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Nieznany błąd",
    };
  }
}
